import React from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import {
  Wrapper,
  Header,
  Inputs,
  InputBox,
  ButtonsDiv,
  SubHeader,
  Label,
  RadioButtonsContainer,
  RadioWrapper,
  RadioLabel,
  NotificationWrapper,
} from "./style";
import { ReactComponent as PinkIcon } from "../../../assets/icons/pinkIcon.svg";
import CustomSelect from "../../common/CustomSelect";
import { InputNumber } from "antd";
import { Button } from "../../common/Button";
import { FormInputs, PayoutModalProps } from "./types";
import ErrorMessage from "../../ErrorMessage";

import { Radio } from "antd";

const payoutOptions = [
  { id: "bank", label: "Bank account" },
  { id: "wallet", label: "Wallet" },
];

const PayoutModal = ({
  accounts,
  onPreview,
  onCloseModal,
  setRequestData,
  getRequestFee,
  businessCurrency,
  setPaymentType,
  getBankId,
}: PayoutModalProps) => {
  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormInputs>();

  const paymentType = watch("payoutDestination");

  const onCancel = () => {
    reset(
      {
        accounts: null,
        amount: null,
      },
      {
        keepErrors: true,
        keepTouched: false,
      },
    );
    onCloseModal();
  };

  const onSubmit: SubmitHandler<FormInputs> = async (formData) => {
    setRequestData(formData);
    await getRequestFee();

    onPreview();
  };

  const handleSelectBank = (data) => {
    getBankId(Number(data));
  };

  const handlePaymentTypeChange = (type) => {
    setPaymentType(type);
  };

  const primaryAccount = accounts?.filter((acc) => acc.primary)

  return (
    <Wrapper onSubmit={handleSubmit(onSubmit)}>
      <Header>Payout request</Header>
      <SubHeader>
        Payouts typically take 1-3 working days, if you're a starter business
        the payout limit is 400k NGN or equivalent.
      </SubHeader>

      <Controller
        name="payoutDestination"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <RadioButtonsContainer>
            {payoutOptions.map((option) => (
              <RadioWrapper key={option.id}>
                <Radio
                  id={option.id}
                  value={option.id}
                  checked={field.value === option.id}
                  onChange={() => {
                    field.onChange(option.id);
                    handlePaymentTypeChange(option.id);
                  }}
                />
                <RadioLabel htmlFor={option.id}>{option.label}</RadioLabel>
              </RadioWrapper>
            ))}
          </RadioButtonsContainer>
        )}
      />

      {errors.payoutDestination && (
        <ErrorMessage message="Please select a payout destination" />
      )}

      {paymentType === 'bank' && (primaryAccount?.length !== 0 ? (
        <Inputs>
          <InputBox>
            <Controller
              name="accounts"
              control={control}
              rules={{ required: true }}
              defaultValue={primaryAccount[0] || null}
              render={({ field }) => (
                <CustomSelect
                  $height="40px"
                  $width="100%"
                  onChange={handleSelectBank}
                  field={field}
                  placeholder="Bank account"
                  options={primaryAccount}
                />
              )}
            />
            {errors.accounts && <ErrorMessage message="Field is required" />}
          </InputBox>

          <InputBox>
            <Controller
              name="amount"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <InputNumber
                  prefix={
                    <Label>
                      <span
                        className={`currency-flag currency-flag-${businessCurrency.toLowerCase()}`}
                      />
                      <span>{businessCurrency}</span>
                    </Label>
                  }
                  {...field}
                  placeholder="Amount"
                  onChange={(value) => field.onChange(value)}
                  formatter={(value) =>
                    `${value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
                  }
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                />
              )}
            />
            {errors.amount && <ErrorMessage message="Field is required" />}
          </InputBox>
        </Inputs>
      ) : (
          <NotificationWrapper>
            <PinkIcon />
            <p>
              Please set a primary bank account in your settings before requesting a payout.
            </p>
          </NotificationWrapper>
      ))}

      {paymentType === "wallet" && (
        <Inputs>
          <NotificationWrapper>
            <PinkIcon />
            <p>
              Transferring funds from Collections Account to {businessCurrency}{" "}
              Wallet.
            </p>
          </NotificationWrapper>

          <InputBox>
            <Controller
              name="amount"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <InputNumber
                  prefix={
                    <Label>
                      <span
                        className={`currency-flag currency-flag-${businessCurrency.toLowerCase()}`}
                      />
                      <span>{businessCurrency}</span>
                    </Label>
                  }
                  {...field}
                  placeholder="Amount"
                  onChange={(value) => field.onChange(value)}
                  formatter={(value) =>
                    `${value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
                  }
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                />
              )}
            />
            {errors.amount && <ErrorMessage message="Field is required" />}
          </InputBox>
        </Inputs>
      )}

      <ButtonsDiv>
        <Button
          label="Cancel"
          type="button"
          theme="alternate"
          height="40px"
          fontSize="12px"
          onClick={onCancel}
        />
        <Button
          label="Request"
          type="submit"
          theme="secondary"
          height="40px"
          fontSize="12px"
          disabled={!paymentType || (paymentType === 'bank' && primaryAccount?.length === 0)}
        />
      </ButtonsDiv>
    </Wrapper>
  );
};

export default PayoutModal;
